// src/components/accueil/CarteRevueFnct.tsx — SCE Issues
// « Revue des FNC-T » à l'accueil (lot 3 du § 14, 22/10 ; maquette
// https://claude.ai/artifact/2nfLQyNb8UjEEBZs5VCmNm, artboard « Accueil de
// Sébastien Vautelin ») : pour qui mène la revue seulement (`meneLaRevueFnct` —
// jusqu'au 03/11 l'administrateur et les testeurs la voyaient aussi). Les deux chiffres qui
// comptent (prêtes à clore, actions en retard) avec ce qui a changé depuis la
// dernière revue ; un clic ouvre la file dans l'onglet Revue.

import { useMemo, type JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { CarteAccueil, PiedCarte } from '@/components/accueil/CarteAccueil'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useFnct } from '@/hooks/useFnct'
import { useRevueFnct } from '@/hooks/useRevueFnct'
import { FNCT_MASQUEE } from '@/config/fnct.config'
import { accesFnct, meneLaRevueFnct } from '@/utils/droitsFnct'
import { aujourdhuiParis, joursEntre } from '@/utils/fnct'
import { calculerRevue, jourDeRevue } from '@/utils/revueFnct'

export function CarteRevueFnct({ hauteurMax }: { hauteurMax?: string }): JSX.Element | null {
  const { user, isFournisseur } = useCurrentUser()
  if (!user || isFournisseur || FNCT_MASQUEE || !accesFnct(user.upn) || !meneLaRevueFnct(user.upn)) return null
  return <Carte hauteurMax={hauteurMax} />
}

function Carte({ hauteurMax }: { hauteurMax?: string }): JSX.Element {
  const navigate = useNavigate()
  const { fncts, actions } = useFnct()
  const { derniere } = useRevueFnct()
  const aujourdhui = aujourdhuiParis()
  const jour = jourDeRevue(derniere)
  const r = useMemo(() => calculerRevue(fncts, actions, aujourdhui, jour), [fncts, actions, aujourdhui, jour])
  const lignes = [
    { file: 'prete', n: r.pretes.length, libelle: 'Prêtes à clore', nouveaux: r.pretes.filter((p) => p.nouveau).length, teinte: 'var(--color-traffic-green)', rouge: false },
    { file: 'retard', n: r.retards.length, libelle: 'Actions en retard', nouveaux: r.retards.filter((a) => a.nouveau).length, teinte: 'var(--color-traffic-red)', rouge: r.retards.length > 0 },
  ]
  const depuis = jour ? joursEntre(jour, aujourdhui) : 0
  const sousTitre = derniere && jour ? `Dernière revue le ${jour.slice(8, 10)}/${jour.slice(5, 7)}, ${depuis === 0 ? 'aujourd’hui' : `il y a ${depuis} j`}` : 'Aucune revue marquée'
  const carte = (
    <CarteAccueil ajuste={!!hauteurMax} titre="Revue des FNC-T" sousTitre={sousTitre} pied={<PiedCarte libelle="Ouvrir la revue" onClick={() => navigate('/fnct?vue=revue')} />}>
      {lignes.map((l) => (
        <button
          key={l.file}
          type="button"
          onClick={() => navigate(`/fnct?vue=revue&file=${l.file}`)}
          className="flex items-center w-full"
          style={{ gap: 14, minHeight: 60, padding: '8px 18px', border: 'none', borderBottom: '1px solid var(--color-gray-10)', background: 'var(--color-white)', fontFamily: 'inherit', color: 'var(--color-black)', textAlign: 'left', cursor: 'pointer' }}
        >
          <span aria-hidden="true" style={{ width: 6, height: 36, borderRadius: 3, flexShrink: 0, background: l.teinte }} />
          <span style={{ minWidth: 30, fontSize: 26, lineHeight: '30px', fontWeight: 500, fontVariantNumeric: 'tabular-nums', color: l.rouge ? 'var(--color-traffic-red)' : 'var(--color-black)' }}>{l.n}</span>
          <span className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 500 }}>{l.libelle}</span>
            {jour && (
              <span className="flex items-center" style={{ gap: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                {l.nouveaux > 0 && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--color-yellow)' }} />}
                {l.nouveaux} depuis la revue
              </span>
            )}
          </span>
          <ChevronRight size={16} color="var(--color-gray-70)" aria-hidden="true" />
        </button>
      ))}
      {r.depuis && (
        <p style={{ margin: 0, padding: '10px 18px', fontSize: 13, lineHeight: '18px', color: 'var(--color-text-secondary)' }}>
          Depuis la revue : {r.depuis.ouvertes.length} FNC-T ouverte{r.depuis.ouvertes.length > 1 ? 's' : ''}, {r.depuis.closes.length ? `${r.depuis.closes.length} close${r.depuis.closes.length > 1 ? 's' : ''}` : 'aucune close'}.
        </p>
      )}
    </CarteAccueil>
  )
  return hauteurMax ? <div style={{ flexShrink: 0, maxHeight: hauteurMax, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{carte}</div> : carte
}
